import { useMemo } from "react";
import API from "../Services/API";
import Model from "./Model";
import State from "./State";
import { toQueryString } from "../Services/helpers";
import { useToasts } from "../Components/Toasts";
import { useSimpleWrapper } from "./SimpleWrapper";
import { invalidateCustomerListCaches } from "./Customers";

const useQuotes = (searchData, alwaysRefresh) => {
    let searchStr = toQueryString(searchData)

    const data = Model({
        alwaysRefresh,
        stateKey: ['quotes', searchStr],
        fetchData: () => API.get('/quotes?'+searchStr),
        returnDataKey: 'quotes',
        saveAdditionalData: (response) => {
            response.map((quote) => {
                State.putIfNotExists(['quote', quote.id], quote);
                return quote;
            });
        },
        defaultLoadingError: 'Failed to load quotes.',
    });

    const groupedQuotes = useMemo(() => {
        if (!data.quotes) {
            return {
                newQuotes: [],
                laterQuotes: [],
                expectingQuotes: [],
                oldQuotes: [],
            };
        }

        return {
            newQuotes: data.quotes.filter(q => ['Processing', 'Submitted', 'Accepted', 'Declined', 'Purchased'].indexOf(q.status) !== -1),
            laterQuotes: data.quotes.filter(q => ['Expecting'].indexOf(q.status) !== -1),
            expectingQuotes: data.quotes.filter(q => ['Estimated', 'Itemised'].indexOf(q.status) !== -1),
            oldQuotes: data.quotes.filter(q => ['Closed', 'Rejected', 'Marketplace'].indexOf(q.status) !== -1),
        };
    }, [data]);

    return {
        ...data,
        ...groupedQuotes,
    }
}

const useQuote = (quoteId, alwaysRefresh = false) => {
    const data = Model({
        alwaysRefresh,
        stateKey: ['quote', quoteId],
        fetchData: () => API.get('/quotes/' + quoteId),
        returnDataKey: 'quote',
        registerAdditionalMethods: (opts) => {
            return {
                update: (data) => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}`, data),
                    null,
                    true,
                ),
                updateEstimate: (data) => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/estimate`, data),
                    null,
                    true,
                ),
                createItemisation: (data) => opts.wrapUpdateCall(
                    () => API.post(`/pricing/itemisations?quoteId=${quoteId}&draft=1`, data),
                    null,
                    true,
                ),
                rejectQuote: () => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/reject`),
                    null,
                    true,
                ),
                closeQuote: () => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/close`),
                    null,
                    true,
                ),
                offerQuote: () => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/offer`),
                    null,
                    true,
                ),
                convert: (data) => opts.wrapUpdateCall(
                    () => {
                        const customerId = opts.model()?.customerId ?? opts.model()?.quote?.customerId;
                        const path = customerId ? `/customers/${customerId}/updateFromQuote` : '/customers/createFromQuote';
                        return API.post(path, {quoteId, ...data}).then((res) => {
                            invalidateCustomerListCaches();
                            return res;
                        });
                    },
                    null,
                    true,
                )
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading the quote.',
    });

    if (data?.quote?.quote) {
        data.quote = {
            ...data.quote.quote,
            customerUrl: data?.quote?.customerUrl ?? data?.customerUrl ?? null,
            area: data?.quote?.area ?? data?.area ?? null,
            distance: data?.quote?.distance ?? data?.distance ?? null,
            thread: data?.quote?.thread ?? data?.thread ?? null,
            itemisations: data?.quote?.itemisations ?? data?.itemisations ?? null,
        };
    }

    return data;
}

const useAddQuote = () => {
    const {showSuccess, showError} = useToasts();

    const addQuote = (data) => {
        return new Promise((resolve, reject) => {
            API.post('/quotes', data)
                .then((d) => {
                    showSuccess('Quote added');
                    resolve(d);
                })
                .catch((err) => {
                    showError(API.parseErrorResponse(err));
                    reject(err);
                })
            })
    }

    return {
        addQuote,
    };
}

const useCloseMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/quotes/close', data),
        'Quotes were closed',
        'Sorry, there was an error closing the quotes',
    )
}

const useRejectMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/quotes/reject', data),
        'Quotes were rejected',
        'Sorry, there was an error rejecting the quotes',
    )
}

const useOfferManyOnMarketplace = () => {
    return useSimpleWrapper(
        (data) => API.post('/quotes/offer', data),
        'Quotes were offered on marketplace',
        'Sorry, there was an error offering the quotes',
    )
}

const quoteStatuses = [
    // 'Processing',
    'Submitted',
    'Expecting',
    'Estimated',
    'Itemised',
    'Accepted',
    'Declined',
    'Rejected',
    'Closed',
    'Marketplace',
    'Purchased',
];

export {
    useQuotes,
    useQuote,
    useAddQuote,
    useCloseMany,
    useRejectMany,
    useOfferManyOnMarketplace,
    quoteStatuses,
}
